import { bbox, envelope, lineString } from '@turf/turf';
import { getFeatureCollection } from './MyCityHelper';

export const MIN_ZOOM = 14;
export const DEFAULT_FILTERS = [
  { name: 'REPORTS' },
  { status_name: 'completed' },
  { status_name: 'inprogress' },
  { status_name: 'pending' },
  { status_name: 'rejected' },
];

export const selectMyCityState = (state) => {
  const enabledModules = state.otherReducer.modules.reduce(
    (accum, item) => {
      if (item.name === 'pois') accum.pois = true;
      if (item.name === 'reports') accum.reports = true;
      if (item.name === 'adopt_a_tree') accum.adopt_a_tree = true;
      return accum;
    },
    { reports: false, pois: false, adopt_a_tree: false }
  );

  return {
    reports: enabledModules.reports ? state.myCityReducer.reports : [],
    pois: enabledModules.pois ? state.myCityReducer.pois : [],
    activeFilters: state.myCityReducer.activeFilters || [],
    adoptATreeModuleEnabled: enabledModules.adopt_a_tree,
    placemarks: state.myCityReducer.placemarks,
    authorityName: state.profileReducer?.profile?.authority?.name || '',
    countryCode: state.profileReducer?.profile?.country?.country_iso_code || '',
  };
};

export const getFitBoundsFromPlacemarks = (placemarks) => {
  if (placemarks?.features?.length > 0) {
    const enveloped = envelope(placemarks);
    const line = lineString(enveloped.geometry.coordinates[0]);
    return bbox(line);
  }
  return null;
};

export const buildBaseCollection = (reports, pois) =>
  reports && reports.length > 0
    ? getFeatureCollection(reports.concat(pois))
    : getFeatureCollection(pois);

export const filterFeatureCollection = (features = [], activeFilters = []) => {
  if (!activeFilters?.length) {
    return [];
  }

  const hasNonReportsFilters = activeFilters.some(
    (f) => f.name && f.name !== 'REPORTS'
  );
  const hasStatusFilters = activeFilters.some((f) => f.status_name);

  if (!hasNonReportsFilters && !hasStatusFilters) {
    return [];
  }

  return features.filter((point) => {
    if (!point) return false;
    const isReport = point.properties.type === 'REPORTS';

    const matchesType =
      hasNonReportsFilters &&
      activeFilters.some(
        (f) => f.name === point.properties.type && f.name !== 'REPORTS'
      );

    const matchesStatus =
      isReport &&
      hasStatusFilters &&
      activeFilters.some((f) => f.status_name === point.properties.status);

    return matchesType || matchesStatus;
  });
};

export const isPointInsideBounds = (bounds, point) => {
  if (!bounds || bounds.length !== 4 || !point || point.length !== 2) {
    return false;
  }
  const [minLng, minLat, maxLng, maxLat] = bounds;
  const [lng, lat] = point;
  const lowerLng = Math.min(minLng, maxLng);
  const upperLng = Math.max(minLng, maxLng);
  const lowerLat = Math.min(minLat, maxLat);
  const upperLat = Math.max(minLat, maxLat);

  return (
    lng >= lowerLng && lng <= upperLng && lat >= lowerLat && lat <= upperLat
  );
};

export const createLoaderStyles = () => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

export const getBoundsFromFeatures = (features = []) => {
  if (!features?.length) {
    return null;
  }

  const featureCollection = {
    type: 'FeatureCollection',
    features,
  };

  return bbox(featureCollection);
};

const WORLD_DIMENSION = 512;
const MAX_ZOOM = 20;

const latRad = (lat) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const clamped = Math.min(Math.max(sin, -0.9999), 0.9999);
  return Math.log((1 + clamped) / (1 - clamped)) / 2;
};

export const estimateZoomFromBounds = (
  bounds,
  { width = 1024, height = 768 } = {}
) => {
  if (!bounds || bounds.length !== 4) {
    return MIN_ZOOM;
  }

  const [minLng, minLat, maxLng, maxLat] = bounds;
  const lngDiff = Math.max(Math.abs(maxLng - minLng), 1e-6);
  const latDiff = Math.max(Math.abs(maxLat - minLat), 1e-6);

  const lngFraction = lngDiff / 360;
  const lngZoom = Math.log2(width / WORLD_DIMENSION / lngFraction);

  const latFraction = Math.max(
    Math.abs(latRad(maxLat) - latRad(minLat)) / Math.PI,
    1e-6
  );
  const latZoom = Math.log2(height / WORLD_DIMENSION / latFraction);

  const zoom = Math.min(Math.max(Math.min(lngZoom, latZoom), MIN_ZOOM), MAX_ZOOM);
  return Number.isFinite(zoom) ? zoom : MIN_ZOOM;
};
